import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import {
  LayoutDashboard,
  Package,
  FileText,
  HelpCircle,
  Globe,
  CreditCard,
  LogOut,
  Menu,
  X,
  Moon,
  Sun,
  ChevronDown,
  Bell,
  User,
  ScrollText,
  Shield,
  Settings,
  FolderOpen,
  HardDrive,
  Briefcase,
  Building2,
  Mail,
  TrendingUp,
} from 'lucide-react';
import api from '../../services/api';

const A2G_LOGO = "https://a2gdesigns.com/icons/icon-512x512.png";
import { Button } from '../ui/button';
import NotificationBell from '../notifications/NotificationBell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/client' },
  { icon: Package, label: 'Services', path: '/client/services' },
  { icon: Globe, label: 'Domains', path: '/client/domains' },
  { icon: HelpCircle, label: 'Support', path: '/client/support' },
  { icon: CreditCard, label: 'Billing', path: '/client/billing', dropdown: [
    { icon: FileText, label: 'Invoices', path: '/client/invoices' },
    { icon: Building2, label: 'By Business', path: '/client/businesses' },
    { icon: CreditCard, label: 'Payment Methods', path: '/client/billing' },
    { icon: ScrollText, label: 'My Quotes', path: '/client/quotes' },
  ]},
  { icon: User, label: 'Profile', path: '/client/profile' },
  { icon: FolderOpen, label: 'Media', dropdown: [
    { icon: FolderOpen, label: 'Files', path: '/client/files' },
    { icon: HardDrive, label: 'Site Backups', path: '/client/site-backups' },
  ]},
];

const SEARCH_NAV_ITEM = { icon: TrendingUp, label: 'Search', path: '/client/search' };
const RESELLER_NAV_ITEM = { icon: Mail, label: 'Reseller Mail', path: '/client/reseller-mail' };

const ClientLayout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  // The Search page only appears once Search Console has been connected for this client.
  const [hasSearch, setHasSearch] = React.useState(false);
  React.useEffect(() => {
    if (!user) return;
    api.get('/gsc/my-metrics').then((r) => setHasSearch(!!r.data?.configured)).catch(() => setHasSearch(false));
  }, [user]);
  const baseItems = hasSearch ? [...navItems.slice(0, 3), SEARCH_NAV_ITEM, ...navItems.slice(3)] : navItems;
  const visibleNavItems = user?.isReseller ? [...baseItems, RESELLER_NAV_ITEM] : baseItems;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#0a0a0c]' : 'bg-gray-50'}`}>
      {/* Subtle Background Effects - Dark mode only */}
      {isDark && (
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/5 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-[100px]" />
        </div>
      )}

      {/* Header */}
      <header className={`sticky top-0 z-50 border-b backdrop-blur-xl ${isDark ? 'border-white/5 bg-[#0a0a0c]/80' : 'border-gray-200 bg-white/80'}`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <Link to="/client" className="flex items-center gap-3 group">
                <img 
                  src={A2G_LOGO} 
                  alt="A2G Designs" 
                  className="h-10 w-auto object-contain"
                />
              </Link>

              {/* Desktop Nav */}
              <nav className="hidden lg:flex items-center gap-1">
                {visibleNavItems.map((item) => {
                  const isActive = location.pathname === item.path || item.dropdown?.some(d => location.pathname === d.path);
                  
                  if (item.dropdown) {
                    return (
                      <DropdownMenu key={item.path}>
                        <DropdownMenuTrigger asChild>
                          <button
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                              isActive
                                ? 'bg-orange-500/20 text-orange-500 shadow-[0_0_20px_rgba(249,115,22,0.2)]'
                                : isDark 
                                  ? 'text-white/60 hover:text-white hover:bg-white/5'
                                  : 'text-slate-600 hover:text-slate-900 hover:bg-gray-100'
                            }`}
                          >
                            <item.icon className="w-4 h-4" />
                            {item.label}
                            <ChevronDown className="w-3 h-3 ml-0.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className={`w-48 ${isDark ? 'bg-slate-900 border-white/10' : 'bg-white border-gray-200'}`}>
                          {item.dropdown.map((subItem) => (
                            <DropdownMenuItem 
                              key={subItem.path} 
                              onClick={() => navigate(subItem.path)} 
                              className={`cursor-pointer ${isDark ? 'text-white/70 hover:text-white hover:bg-white/10 focus:bg-white/10 focus:text-white' : 'text-slate-600 hover:text-slate-900 hover:bg-gray-100 focus:bg-gray-100'}`}
                            >
                              <subItem.icon className="w-4 h-4 mr-2" />
                              {subItem.label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    );
                  }

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                        isActive
                          ? 'bg-orange-500/20 text-orange-500 shadow-[0_0_20px_rgba(249,115,22,0.2)]'
                          : isDark
                            ? 'text-white/60 hover:text-white hover:bg-white/5'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-gray-100'
                      }`}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  );
                })}
              </nav>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={toggleTheme}
                className={`p-2.5 rounded-xl transition-all duration-300 group ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'}`}
              >
                {isDark ? (
                  <Sun className="w-5 h-5 text-orange-400 group-hover:rotate-45 transition-transform duration-300" />
                ) : (
                  <Moon className="w-5 h-5 text-slate-700 group-hover:text-slate-900 transition-colors" />
                )}
              </button>

              <NotificationBell isDark={isDark} />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className={`flex items-center gap-3 p-2 rounded-xl transition-all duration-300 ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'}`}>
                    <div className="w-9 h-9 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-[0_0_20px_rgba(249,115,22,0.3)]">
                      <span className="text-white text-sm font-bold">
                        {user?.firstName?.[0]}{user?.lastName?.[0]}
                      </span>
                    </div>
                    <div className="hidden sm:block text-left">
                      <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {user?.firstName}
                      </p>
                      <p className={`text-xs flex items-center gap-1 ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
                        Client
                        {user?.isReseller && (
                          <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-500 text-[10px] font-medium rounded-full">Reseller</span>
                        )}
                      </p>
                    </div>
                    <ChevronDown className={`w-4 h-4 hidden sm:block ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className={`w-56 ${isDark ? 'bg-slate-900 border-white/10' : 'bg-white border-gray-200'}`}>
                  <div className={`px-3 py-3 border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{user?.firstName} {user?.lastName}</p>
                    <p className={`text-sm ${isDark ? 'text-white/50' : 'text-slate-500'}`}>{user?.email}</p>
                    {(user?.role === 'superuser' || user?.role === 'admin') && (
                      <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-orange-500/20 text-orange-500 text-xs font-medium rounded-full">
                        <Shield className="w-3 h-3" />
                        {user?.role === 'superuser' ? 'Super Admin' : 'Admin'}
                      </span>
                    )}
                    {user?.isReseller && (
                      <span className="inline-flex items-center gap-1 mt-1 ml-1 px-2 py-0.5 bg-blue-500/20 text-blue-500 text-xs font-medium rounded-full">
                        <Mail className="w-3 h-3" />
                        Reseller
                      </span>
                    )}
                  </div>
                  
                  {/* Admin-only links */}
                  {(user?.role === 'superuser' || user?.role === 'admin') && (
                    <>
                      <div className="p-1">
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin')}
                          className={`cursor-pointer rounded-lg ${isDark ? 'text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 focus:bg-orange-500/10' : 'text-orange-600 hover:text-orange-700 hover:bg-orange-50 focus:bg-orange-50'}`}
                        >
                          <LayoutDashboard className="w-4 h-4 mr-2" />
                          Admin Dashboard
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin/settings')}
                          className={`cursor-pointer rounded-lg ${isDark ? 'text-white/70 hover:text-white hover:bg-white/10 focus:bg-white/10 focus:text-white' : 'text-slate-600 hover:text-slate-900 hover:bg-gray-100 focus:bg-gray-100'}`}
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Admin Settings
                        </DropdownMenuItem>
                      </div>
                      <DropdownMenuSeparator className={isDark ? 'bg-white/5' : 'bg-gray-100'} />
                    </>
                  )}
                  
                  <div className="p-1">
                    <DropdownMenuItem 
                      onClick={() => navigate('/client/profile')}
                      className={`cursor-pointer rounded-lg ${isDark ? 'text-white/70 hover:text-white hover:bg-white/10 focus:bg-white/10 focus:text-white' : 'text-slate-600 hover:text-slate-900 hover:bg-gray-100 focus:bg-gray-100'}`}
                    >
                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/client/billing')}
                      className={`cursor-pointer rounded-lg ${isDark ? 'text-white/70 hover:text-white hover:bg-white/10 focus:bg-white/10 focus:text-white' : 'text-slate-600 hover:text-slate-900 hover:bg-gray-100 focus:bg-gray-100'}`}
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Billing
                    </DropdownMenuItem>
                  </div>
                  <DropdownMenuSeparator className={isDark ? 'bg-white/5' : 'bg-gray-100'} />
                  <div className="p-1">
                    <DropdownMenuItem 
                      onClick={handleLogout} 
                      className="cursor-pointer text-red-500 hover:text-red-400 hover:bg-red-500/10 focus:bg-red-500/10 focus:text-red-400 rounded-lg"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              <button
                onClick={() => setMobileMenuOpen(true)}
                className={`lg:hidden p-2.5 rounded-xl transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'}`}
              >
                <Menu className={`w-6 h-6 ${isDark ? 'text-white/60' : 'text-slate-600'}`} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ── Bar Sign Strip ── */}
      <div
        className="w-full relative overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #0b0b10 0%, #13131c 100%)',
          borderBottom: '1px solid rgba(249,115,22,0.18)',
          boxShadow: '0 4px 40px rgba(249,115,22,0.07), inset 0 1px 0 rgba(255,255,255,0.03)',
        }}
      >
        {/* Subtle scanline overlay */}
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.012) 2px, rgba(255,255,255,0.012) 4px)' }} />

        <div className="flex items-center justify-center py-2.5 gap-6 relative">
          {/* Left rail */}
          <div className="hidden sm:flex items-center gap-2.5">
            <div className="h-px w-8 md:w-24 lg:w-48" style={{ background: 'linear-gradient(90deg, transparent, rgba(249,115,22,0.55))' }} />
            <span style={{ color: 'rgba(249,115,22,0.45)', fontSize: '7px', letterSpacing: '3px' }}>◆◆◆</span>
            <div className="h-px w-4" style={{ background: 'rgba(249,115,22,0.35)' }} />
          </div>

          {/* Main neon text */}
          <span
            className="font-black uppercase select-none"
            style={{
              fontSize: 'clamp(0.65rem, 1.8vw, 0.9rem)',
              letterSpacing: '0.45em',
              color: '#ffab62',
              textShadow: '0 0 6px #ff8c2a, 0 0 14px #ff8c2a, 0 0 28px rgba(249,115,22,0.7), 0 0 55px rgba(249,115,22,0.35)',
              animation: 'neonPulse 3.5s ease-in-out infinite',
            }}
          >
            Client&nbsp;&nbsp;Portal
          </span>

          {/* Right rail */}
          <div className="hidden sm:flex items-center gap-2.5">
            <div className="h-px w-4" style={{ background: 'rgba(249,115,22,0.35)' }} />
            <span style={{ color: 'rgba(249,115,22,0.45)', fontSize: '7px', letterSpacing: '3px' }}>◆◆◆</span>
            <div className="h-px w-8 md:w-24 lg:w-48" style={{ background: 'linear-gradient(270deg, transparent, rgba(249,115,22,0.55))' }} />
          </div>
        </div>

        {/* Bottom glow line */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/3 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(249,115,22,0.5), transparent)' }} />
      </div>

      <style>{`
        @keyframes neonPulse {
          0%, 100% { text-shadow: 0 0 6px #ff8c2a, 0 0 14px #ff8c2a, 0 0 28px rgba(249,115,22,0.7), 0 0 55px rgba(249,115,22,0.35); }
          50%       { text-shadow: 0 0 4px #ff8c2a, 0 0 10px #ff8c2a, 0 0 20px rgba(249,115,22,0.5), 0 0 38px rgba(249,115,22,0.2); }
        }
      `}</style>
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
          <aside className={`fixed top-0 right-0 w-72 h-full z-50 ${isDark ? 'bg-[#0a0a0c] border-l border-white/5' : 'bg-white border-l border-gray-200'}`}>
            <div className={`flex items-center justify-between h-16 px-4 border-b ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
              <span className={`font-bold text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>Menu</span>
              <button 
                onClick={() => setMobileMenuOpen(false)}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'}`}
              >
                <X className={`w-6 h-6 ${isDark ? 'text-white/60' : 'text-slate-600'}`} />
              </button>
            </div>
            <nav className="p-4 space-y-2">
              {visibleNavItems.map((item) => {
                if (item.dropdown) {
                  return (
                    <div key={item.path} className="space-y-1">
                      <p className={`px-3 py-2 text-xs font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-slate-400'}`}>{item.label}</p>
                      {item.dropdown.map((subItem) => {
                        const isSubActive = location.pathname === subItem.path;
                        return (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-300 ${
                              isSubActive
                                ? 'bg-orange-500/20 text-orange-500'
                                : isDark
                                  ? 'text-white/60 hover:text-white hover:bg-white/5'
                                  : 'text-slate-600 hover:text-slate-900 hover:bg-gray-100'
                            }`}
                          >
                            <subItem.icon className="w-5 h-5" />
                            <span className="font-medium">{subItem.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  );
                }

                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-300 ${
                      isActive
                        ? 'bg-orange-500/20 text-orange-500'
                        : isDark
                          ? 'text-white/60 hover:text-white hover:bg-white/5'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-gray-100'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>
            <div className={`absolute bottom-0 left-0 right-0 p-4 border-t ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
              <button 
                onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                className="flex items-center gap-3 w-full px-3 py-3 rounded-xl text-red-500 hover:bg-red-500/10 transition-all duration-300"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="relative max-w-7xl mx-auto px-4 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className={`border-t mt-12 ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className={`text-sm ${isDark ? 'text-white/40' : 'text-slate-500'}`}>© 2024 A2G Designs. All rights reserved.</p>
            <div className={`flex items-center gap-4 text-sm ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
              <a href="/privacy-policy" className={`transition-colors ${isDark ? 'hover:text-white' : 'hover:text-slate-900'}`}>Privacy</a>
              <a href="/terms" className={`transition-colors ${isDark ? 'hover:text-white' : 'hover:text-slate-900'}`}>Terms</a>
              <a href="/client/support" className={`transition-colors ${isDark ? 'hover:text-white' : 'hover:text-slate-900'}`}>Support</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClientLayout;
